<script setup>
import { ref } from 'vue';
import { Collapse } from 'vue-collapsed';

defineProps({
  questions: {
    type: Array,
    default: () => [],
  },
  inSidebar: {
    type: Boolean,
    default: false,
  },
});

const expandedItem = ref(null);
function setExpandedItem(itemName) {
  expandedItem.value = expandedItem.value === itemName ? null : itemName;
}
</script>

<template>
  <div class="ctr-questions" :class="{ container: !inSidebar }">
    <div
      v-for="(question, index) in questions"
      :key="`question-${index}`"
      class="ctr-questions__item"
    >
      <button
        v-if="question?.question"
        type="button"
        class="ctr-questions__button link"
        :class="{
          'link--active': expandedItem === question.question,
        }"
        @click="setExpandedItem(question.question)"
      >
        <span class="ctr-questions__font ctr-questions__font--button">
          {{ $tp(question.question) }}
        </span>

        <span
          class="ctr-questions__toggle-icon"
          :class="{
            'ctr-questions__toggle-icon--open':
              expandedItem === question.question,
          }"
        />
      </button>

      <Collapse :when="expandedItem === question.question">
        <UiHtmlContent
          v-if="question.answer"
          class="ctr-questions__font ctr-questions__font--text"
          :content="question.answer"
          custom-font
        />
      </Collapse>
    </div>
  </div>
</template>

<style scoped lang="scss">
.ctr-questions {
  $parent: &;

  display: flex;
  flex-direction: column;

  &__item {
    padding: em(35) 0;

    &:first-child {
      padding-top: 0;
    }

    &:last-child {
      padding-bottom: 0;
    }

    &:not(:last-child) {
      border-bottom: 1px solid $color-gray-1;
    }

    @include media-breakpoint-up(sm) {
      padding: em(38) 0;
    }
  }

  &__button {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: em(54);
    width: 100%;

    @include media-breakpoint-down(sm) {
      gap: em(19);
    }
  }

  &__font {
    &--button {
      font-size: em(32);
      line-height: em(35, 32);

      @include media-breakpoint-down(sm) {
        font-size: em(20);
        line-height: em(22, 20);
      }
    }

    &--text {
      padding-top: em(30, 18);
      font-weight: 250;
      font-size: em(18);
      line-height: em(23, 18);

      @include media-breakpoint-down(sm) {
        font-size: em(16);
        line-height: em(21, 16);
      }
    }
  }

  &__toggle-icon {
    position: relative;
    flex-shrink: 0;
    display: inline-block;
    width: em(24);
    height: em(24);

    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 0;
      width: 100%;
      height: 2px;
      background: $color-red-1;
      transform: translateY(-50%);
      transition: transform $time-fast;
    }

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 2px;
      height: 100%;
      background: $color-red-1;
      transform: translate(-50%, -50%);
      transition: transform $time-fast;
    }

    &--open::after {
      transform: translate(-50%, -50%) rotate(90deg);
    }
  }
}
</style>
